import { ApiError } from '@/utils/ApiError';
import { type ErrorResponseBody } from '@/utils/readErrorBody';

// Mirrors the codes declared by the auth route definitions in web-app-alpha
// (see their OpenAPI error responses), plus app-only codes for WAF blocks and
// unknown failures.
const authErrorCodes = [
  '2FA/INVALID-MOBILE-PHONE',
  '2FA/ALREADY-EXISTS',
  '2FA/TOO-MANY-REQUESTS',
  '2FA/EXPIRED-OTP',
  '2FA/TOO-MANY-FAILED-ATTEMPTS',
  '2FA/INVALID-OTP',
  'AUTH/INVALID-EMAIL-OR-PASSWORD',
  'AUTH/ACCOUNT-CLOSED',
  'AUTH/FORBIDDEN',
  'AUTH/TOO-MANY-ATTEMPTS',
  'AUTH/UNKNOWN-ERROR',
] as const;

export type AuthErrorCode = (typeof authErrorCodes)[number];

export const isAuthErrorCode = (value: unknown): value is AuthErrorCode =>
  authErrorCodes.some((code) => code === value);

export class AuthApiError extends ApiError {
  declare readonly code: AuthErrorCode;

  constructor(code: AuthErrorCode, statusCode: number, message: string = code) {
    super(message, statusCode, code);
    this.name = 'AuthApiError';
  }
}

// Fallback for API responses without an `errorCode` - can be removed once verified that backend doesn't sends these
const legacyAuthErrorMessages = {
  'Not a valid mobile phone number.': '2FA/INVALID-MOBILE-PHONE',
  '2FA is already set up for this user': '2FA/ALREADY-EXISTS',
  'Too many OTPs created in this session': '2FA/TOO-MANY-REQUESTS',
  'Sorry, your code has expired. Please ask for a new code.': '2FA/EXPIRED-OTP',
  'Too many failed attempts for this OTP': '2FA/TOO-MANY-FAILED-ATTEMPTS',
  "Sorry, we don't recognise that code. Try entering it again or ask for a new code.":
    '2FA/INVALID-OTP',
  'Invalid email or password': 'AUTH/INVALID-EMAIL-OR-PASSWORD',
  'AUTH/INVALID-EMAIL-OR-PASSWORD': 'AUTH/INVALID-EMAIL-OR-PASSWORD',
  'AUTH/ACCOUNT-CLOSED': 'AUTH/ACCOUNT-CLOSED',
  'AUTH/FORBIDDEN': 'AUTH/FORBIDDEN',
  'AUTH/TOO-MANY-ATTEMPTS': 'AUTH/TOO-MANY-ATTEMPTS',
  'AUTH/UNKNOWN-ERROR': 'AUTH/UNKNOWN-ERROR',
} as const satisfies Record<string, AuthErrorCode>;

const getLegacyAuthErrorCode = (
  errorMessage: unknown,
): AuthErrorCode | undefined => {
  if (typeof errorMessage !== 'string') return undefined;
  return Object.entries(legacyAuthErrorMessages).find(([message]) =>
    errorMessage.includes(message),
  )?.[1];
};

/**
 * Resolves a failed auth response into typed error -
 * recognised `errorCode` wins, then `statusFallbackCodes` (e.g.
 * WAF blocks, which don't have a code), then the legacy message mapping
 */
export const createAuthApiError = (
  statusCode: number,
  { error, errorCode }: ErrorResponseBody,
  statusFallbackCodes: Partial<Record<number, AuthErrorCode>> = {},
): AuthApiError => {
  const code =
    (isAuthErrorCode(errorCode) ? errorCode : undefined) ??
    statusFallbackCodes[statusCode] ??
    getLegacyAuthErrorCode(error) ??
    'AUTH/UNKNOWN-ERROR';
  const message = typeof error === 'string' && error ? error : code;
  return new AuthApiError(code, statusCode, message);
};

// Returns the auth error code for anything thrown by an auth request
export const getAuthErrorCode = (error: unknown): AuthErrorCode =>
  error instanceof AuthApiError ? error.code : 'AUTH/UNKNOWN-ERROR';
